import { STRATEGIES } from './strategyData';

export const PORTFOLIO_TYPE_TO_STRATEGY_ID = {
  PRINCIPAL_PROTECTION: 'principal-protection',
  REGULAR_INCOME: 'regular-income',
  TARGET_RETURN_PORTFOLIO: 'target-return',
  HIGH_RETURN: 'high-return',
  LIQUIDITY: 'liquidity',
  MODIFIED_RISK: 'modified-risk',
};

export const STRATEGY_CHART_COLORS = {
  'principal-protection': '#38bdf8',
  'regular-income': '#4ade80',
  'target-return': '#fbbf24',
  'high-return': '#f87171',
  liquidity: '#67e8f9',
  'modified-risk': '#a78bfa',
};

export const MATURITY_BUCKET_ORDER = [
  '<1 year',
  '1-3 years',
  '3-5 years',
  '5-10 years',
  '10+ years',
  'Unknown',
];

export const MATURITY_BUCKET_COLORS = ['#1D9E75', '#378ADD', '#639922', '#BA7517', '#D4537E', '#888780'];

export function formatPortfolioType(enumValue) {
  if (!enumValue) return '';
  return enumValue
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .replace(/\bAnd\b/g, '&');
}

export function portfolioTypeToStrategy(portfolioType) {
  const id = PORTFOLIO_TYPE_TO_STRATEGY_ID[portfolioType];
  return STRATEGIES.find((s) => s.id === id) || null;
}

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function yearsToMaturity(maturityDate, now = new Date()) {
  const mat = parseDate(maturityDate);
  if (!mat) return null;
  return Math.max(0, (mat - now) / (365.25 * 24 * 60 * 60 * 1000));
}

export function getMaturityBucket(maturityDate, now = new Date()) {
  const years = yearsToMaturity(maturityDate, now);
  if (years == null) return 'Unknown';
  if (years < 1) return '<1 year';
  if (years < 3) return '1-3 years';
  if (years < 5) return '3-5 years';
  if (years < 10) return '5-10 years';
  return '10+ years';
}

/** Estimated annual coupon income (INR) from face value × coupon %. */
export function computeAnnualIncome(holding) {
  const coupon = Number(holding?.coupon);
  if (Number.isNaN(coupon)) return 0;
  const qty = Number(holding?.quantity);
  if (qty > 0) return qty * 100 * (coupon / 100);
  const mv = Number(holding?.totalMarketValue) || 0;
  return mv * (coupon / 100);
}

export function buildPortfolioAnalytics(holdings) {
  const rows = Array.isArray(holdings) ? holdings : [];
  const totalMV = rows.reduce((sum, h) => sum + (Number(h.totalMarketValue) || 0), 0);

  const annualIncome = rows.reduce((sum, h) => sum + computeAnnualIncome(h), 0);

  let yieldWeighted = 0;
  let couponWeighted = 0;
  let weightForYield = 0;
  let weightForCoupon = 0;

  const bucketMV = Object.fromEntries(MATURITY_BUCKET_ORDER.map((b) => [b, 0]));
  rows.forEach((h) => {
    const mv = Number(h.totalMarketValue) || 0;
    const bucket = getMaturityBucket(h.maturityDate);
    bucketMV[bucket] = (bucketMV[bucket] || 0) + mv;
    if (mv > 0) {
      if (h.yield != null && !Number.isNaN(Number(h.yield))) {
        yieldWeighted += Number(h.yield) * mv;
        weightForYield += mv;
      }
      if (h.coupon != null && !Number.isNaN(Number(h.coupon))) {
        couponWeighted += Number(h.coupon) * mv;
        weightForCoupon += mv;
      }
    }
  });

  const maturityPct = Object.fromEntries(
    Object.entries(bucketMV).map(([key, mv]) => [key, totalMV > 0 ? (mv / totalMV) * 100 : 0])
  );

  const issuerMV = {};
  rows.forEach((h) => {
    const issuer = h.issuer?.trim() || 'Unknown issuer';
    issuerMV[issuer] = (issuerMV[issuer] || 0) + (Number(h.totalMarketValue) || 0);
  });

  const allIssuers = Object.entries(issuerMV)
    .sort(([, a], [, b]) => b - a)
    .map(([issuer, mv]) => ({
      issuer,
      marketValue: mv,
      weightPct: totalMV > 0 ? (mv / totalMV) * 100 : 0,
    }));

  const byStrategy = {};
  rows.forEach((h) => {
    const type = h.type || 'UNKNOWN';
    if (!byStrategy[type]) {
      byStrategy[type] = {
        holdings: [],
        marketValue: 0,
        couponWeighted: 0,
        yieldWeighted: 0,
      };
    }
    const group = byStrategy[type];
    const mv = Number(h.totalMarketValue) || 0;
    group.holdings.push(h);
    group.marketValue += mv;
    if (h.coupon != null && !Number.isNaN(Number(h.coupon))) {
      group.couponWeighted += Number(h.coupon) * mv;
    }
    if (h.yield != null && !Number.isNaN(Number(h.yield))) {
      group.yieldWeighted += Number(h.yield) * mv;
    }
  });

  const strategyBreakdown = Object.entries(byStrategy)
    .map(([type, group]) => ({
      type,
      strategy: portfolioTypeToStrategy(type),
      count: group.holdings.length,
      marketValue: group.marketValue,
      weightPct: totalMV > 0 ? (group.marketValue / totalMV) * 100 : 0,
      avgCoupon: group.marketValue > 0 ? group.couponWeighted / group.marketValue : null,
      avgYield: group.marketValue > 0 ? group.yieldWeighted / group.marketValue : null,
      annualIncome: group.holdings.reduce((sum, h) => sum + computeAnnualIncome(h), 0),
    }))
    .sort((a, b) => b.marketValue - a.marketValue);

  let maturityWeightedSum = 0;
  let maturityWeight = 0;
  rows.forEach((h) => {
    const mv = Number(h.totalMarketValue) || 0;
    const years = yearsToMaturity(h.maturityDate);
    if (years != null && mv > 0) {
      maturityWeightedSum += years * mv;
      maturityWeight += mv;
    }
  });

  const activeStrategies = strategyBreakdown.filter((s) => s.count > 0).length;
  const maxIssuerWeight = allIssuers[0]?.weightPct || 0;

  return {
    totalMV,
    annualIncome,
    maturityPct,
    allIssuers,
    maxIssuerWeight,
    strategyBreakdown,
    weightedAvgMaturityYears: maturityWeight > 0 ? maturityWeightedSum / maturityWeight : null,
    holdingsCount: rows.length,
    activeStrategies,
    avgYield: weightForYield > 0 ? yieldWeighted / weightForYield : null,
    avgCoupon: weightForCoupon > 0 ? couponWeighted / weightForCoupon : null,
  };
}

export function buildPortfolioInsights(analytics) {
  const insights = [];

  if (analytics.holdingsCount === 0) return insights;

  if (analytics.annualIncome > 0) {
    const monthly = analytics.annualIncome / 12;
    insights.push({
      id: 'income',
      tone: 'positive',
      title: 'Estimated coupon income',
      body: `Your holdings are on track to generate about ₹${Math.round(analytics.annualIncome).toLocaleString('en-IN')} per year (~₹${Math.round(monthly).toLocaleString('en-IN')}/month) from stated coupons.`,
    });
  }

  if (analytics.weightedAvgMaturityYears != null) {
    const yrs = analytics.weightedAvgMaturityYears;
    const label = yrs < 1.5
      ? 'short-term and relatively liquid'
      : yrs < 5
        ? 'medium-term — balanced between yield and reinvestment risk'
        : 'longer-dated — higher duration sensitivity to rate moves';
    insights.push({
      id: 'maturity',
      tone: 'neutral',
      title: 'Maturity profile',
      body: `Weighted average maturity is ${yrs.toFixed(1)} years. The portfolio is ${label}.`,
    });
  }

  if (analytics.maxIssuerWeight > 25) {
    insights.push({
      id: 'concentration',
      tone: 'caution',
      title: 'Issuer concentration',
      body: `Your largest issuer represents ${analytics.maxIssuerWeight.toFixed(1)}% of portfolio value. Consider spreading across more issuers to reduce single-name risk.`,
    });
  } else if (analytics.holdingsCount >= 3) {
    insights.push({
      id: 'diversification',
      tone: 'positive',
      title: 'Diversification',
      body: `Holdings are spread across ${analytics.holdingsCount} bonds in ${analytics.activeStrategies} strateg${analytics.activeStrategies === 1 ? 'y' : 'ies'} — no single issuer dominates the book.`,
    });
  }

  const shortPct = (analytics.maturityPct['<1 year'] || 0) + (analytics.maturityPct['1-3 years'] || 0);
  if (shortPct > 60) {
    insights.push({
      id: 'liquidity',
      tone: 'neutral',
      title: 'Liquidity tilt',
      body: `${shortPct.toFixed(0)}% matures within 3 years — useful if you may need capital back soon, but reinvestment yield may change when rates move.`,
    });
  }

  return insights.slice(0, 4);
}
